// ── SINGLE SOURCE OF TRUTH for all plan pricing ──────────────────────────────
// SiteProof has two pricing structures live at once:
//
//   legacy_flat_v1 (grandfathered): flat £50/site/month or £40/site/month annual.
//     Existing Stripe Price objects use billing_scheme='per_unit' with
//     unit_amount=5000 (monthly) and 48000 (annual, ie £480/year/site).
//
//   graduated_v1 (new signups from 2026-05): graduated tiered pricing. Each
//     tier pays its own rate for the sites in that tier, summed. New Stripe
//     Price objects use billing_scheme='tiered' with tiers_mode='graduated'.
//
// Pricing version is stored on companies.pricing_version. New signups go
// through create-checkout-session which picks the V2 Price IDs; the webhook
// stamps companies.pricing_version='graduated_v1' on the resulting Stripe
// event. Existing rows were backfilled to 'legacy_flat_v1' in
// 20260511100000_add_pricing_version_to_companies.sql.

// ── Pricing versions ────────────────────────────────────────────────────────
export const PRICING_VERSION_LEGACY = 'legacy_flat_v1';
export const PRICING_VERSION_GRADUATED = 'graduated_v1';

// ── Legacy flat-rate constants (still in use for legacy_flat_v1 customers) ──
export const MONTHLY_PRICE_PER_SITE = 50;
export const ANNUAL_PRICE_PER_SITE = 40;
export const MIN_SITES = 2;
export const ANNUAL_DISCOUNT_PERCENT = 20;

// ── Graduated tiered pricing (graduated_v1) ─────────────────────────────────
// Display values are £/site/month. The Stripe Price objects encode these as
// pence per month (monthly Price) or pence per year (annual Price; values
// below × 12 × 100).
export const GRADUATED_MONTHLY_TIERS = [
  { upTo: 10,       pricePerSite: 45 },  // 2–10 sites
  { upTo: 25,       pricePerSite: 40 },  // 11–25
  { upTo: 50,       pricePerSite: 35 },  // 26–50
  { upTo: 100,      pricePerSite: 30 },  // 51–100
  { upTo: Infinity, pricePerSite: 25 },  // 101+
];

export const GRADUATED_ANNUAL_TIERS = [
  { upTo: 10,       pricePerSite: 38 },  // 2–10 sites  (15% off £45, rounded)
  { upTo: 25,       pricePerSite: 34 },  // 11–25       (15% off £40)
  { upTo: 50,       pricePerSite: 30 },  // 26–50       (15% off £35)
  { upTo: 100,      pricePerSite: 26 },  // 51–100      (15% off £30)
  { upTo: Infinity, pricePerSite: 21 },  // 101+        (15% off £25)
];

// ── Price calculation helpers ───────────────────────────────────────────────

// Legacy flat-rate calc: kept for grandfathered customer displays.
export const calculatePrice = (siteCount, billingCycle) => {
  const count = Math.max(siteCount || 0, MIN_SITES);
  const pricePerSite = billingCycle === 'annual' ? ANNUAL_PRICE_PER_SITE : MONTHLY_PRICE_PER_SITE;
  const monthlyTotal = count * pricePerSite;
  const annualTotal = count * ANNUAL_PRICE_PER_SITE * 12;
  return { siteCount: count, pricePerSite, monthlyTotal, annualTotal };
};

// Graduated calc: returns the per-month cost (annual cycles return the
// monthly-equivalent) plus a `breakdown` array describing how many sites
// fell into each tier and at what rate, so the UI can render "10 × £45 +
// 15 × £40 = £1,050/month".
export const calculateGraduatedPrice = (siteCount, billingCycle) => {
  const count = Math.max(siteCount || 0, MIN_SITES);
  const tiers = billingCycle === 'annual' ? GRADUATED_ANNUAL_TIERS : GRADUATED_MONTHLY_TIERS;

  let remaining = count;
  let cumulativeFloor = 0;
  let monthlyTotal = 0;
  const breakdown = [];

  for (const tier of tiers) {
    if (remaining <= 0) break;
    const tierCapacity = tier.upTo === Infinity ? remaining : tier.upTo - cumulativeFloor;
    const sitesInTier = Math.min(remaining, tierCapacity);
    if (sitesInTier > 0) {
      breakdown.push({ sites: sitesInTier, pricePerSite: tier.pricePerSite });
      monthlyTotal += sitesInTier * tier.pricePerSite;
      remaining -= sitesInTier;
    }
    cumulativeFloor = tier.upTo;
  }

  // Effective blended rate — useful for "headline" displays where a single
  // £/site number reads better than a multi-tier breakdown.
  const effectivePerSite = count > 0 ? monthlyTotal / count : 0;
  const annualTotal = billingCycle === 'annual' ? monthlyTotal * 12 : monthlyTotal * 12;

  return {
    siteCount: count,
    monthlyTotal,
    annualTotal,
    breakdown,
    effectivePerSite,
  };
};

// Pricing-version-aware calc. Use this when displaying an existing
// customer's bill so legacy subscribers see flat-rate maths and new
// subscribers see graduated.
export const calculatePriceForVersion = (siteCount, billingCycle, pricingVersion) => {
  if (pricingVersion === PRICING_VERSION_GRADUATED) {
    return { version: PRICING_VERSION_GRADUATED, ...calculateGraduatedPrice(siteCount, billingCycle) };
  }
  return { version: PRICING_VERSION_LEGACY, ...calculatePrice(siteCount, billingCycle) };
};
